/**
 * Human labels for event types.
 *
 * The bitácora is the audit trail, and an audit trail nobody can read is a compliance artefact
 * rather than a tool. `venta.registrada` means nothing to the person who has to work out what
 * happened on the 14th; "Venta" does.
 *
 * Typed against the registry, so adding an event type without a label fails the build instead
 * of quietly showing a slug to whoever opens the screen.
 */

import type { EventType } from './registry.js';

export const ETIQUETA_EVENTO: Record<EventType, string> = {
  'usuario.creado': 'Usuario creado',
  'usuario.actualizado': 'Usuario modificado',
  'usuario.pin_cambiado': 'PIN cambiado',
  'usuario.desactivado': 'Usuario desactivado',
  'usuario.reactivado': 'Usuario reactivado',

  'sesion.iniciada': 'Inicio de sesión',
  'sesion.pin_incorrecto': 'PIN incorrecto',

  'dispositivo.registrado': 'Equipo registrado',
  'dispositivo.revocado': 'Equipo revocado',
  'dispositivo.reactivado': 'Equipo dado de alta otra vez',

  'producto.creado': 'Producto nuevo',
  'producto.actualizado': 'Producto modificado',
  'producto.desactivado': 'Producto desactivado',
  'producto.reactivado': 'Producto reactivado',
  'unidad_venta.creada': 'Presentación nueva',
  'unidad_venta.actualizada': 'Presentación modificada',
  'unidad_venta.eliminada': 'Presentación eliminada',
  'lista_precio.creada': 'Lista de precios nueva',
  'lista_precio.actualizada': 'Lista de precios modificada',
  'lista_precio.eliminada': 'Lista de precios eliminada',
  'precio.fijado': 'Precio fijado',
  'oferta.creada': 'Oferta',
  'oferta.terminada': 'Oferta terminada',

  'proveedor.creado': 'Proveedor nuevo',
  'proveedor.actualizado': 'Proveedor modificado',
  'proveedor.desactivado': 'Proveedor desactivado',
  'compra.registrada': 'Compra recibida',
  'compra.anulada': 'Compra anulada',
  'compra.costo_corregido': 'Costo de compra corregido',
  'compra.cantidad_corregida': 'Cantidad de compra corregida',
  'compra.forma_pago_corregida': 'Forma de pago de compra corregida',
  'compra.devuelta': 'Devolución a proveedor',
  'compra.pagada': 'Abono a proveedor',
  'compra.pago_anulado': 'Abono a proveedor anulado',
  'inventario.saldo_inicial': 'Inventario inicial',
  'inventario.costo_corregido': 'Costo de inventario corregido',
  'compra.saldo_inicial': 'Deuda anterior con un proveedor',

  'cliente.creado': 'Cliente nuevo',
  'cliente.actualizado': 'Cliente modificado',
  'cliente.desactivado': 'Cliente desactivado',
  'cliente.saldo_inicial': 'Saldo anterior de un cliente',
  'abono.registrado': 'Abono de cliente',
  'abono.anulado': 'Abono anulado',
  'incobrable.registrado': 'Fiado dado por perdido',
  'incobrable.anulado': 'Fiado perdido, deshecho',
  'pedido.tomado': 'Pedido tomado',
  'pedido.actualizado': 'Pedido modificado',
  'pedido.cancelado': 'Pedido cancelado',
  'jornada.abierta': 'Salida a ruta',
  'jornada.liquidada': 'Liquidación de ruta',

  'inventario.reempaque': 'Reempaque',
  'inventario.conteo': 'Conteo físico',
  'inventario.salida': 'Salida sin venta',
  'inventario.traslado': 'Traslado',

  'caja.abierta': 'Caja abierta',
  'caja.movimiento': 'Movimiento de caja',
  'caja.entregada': 'Entrega de caja',
  'caja.cerrada': 'Corte de caja',
  'caja.apertura_corregida': 'Apertura de caja corregida',
  'caja.corte_corregido': 'Corte de caja corregido',
  'efectivo.recibido': 'Efectivo recibido',
  'efectivo.arqueo': 'Arqueo de efectivo general',
  'cuenta.conciliada': 'Cuenta conciliada',
  'efectivo.depositado': 'Depósito en el banco',
  'efectivo.deposito_anulado': 'Depósito anulado',
  'efectivo.sacado_del_banco': 'Sacado del banco',
  'banco.transferido': 'Transferencia entre cuentas',
  'banco.transferencia_anulada': 'Transferencia entre cuentas anulada',
  'semana.cuadrada': 'Semana cuadrada',

  'venta.registrada': 'Venta',
  'venta.anulada': 'Venta anulada',
  'pago.confirmado': 'Transferencia confirmada',
  'pago.rechazado': 'Transferencia rechazada',
  'devolucion.registrada': 'Devolución de cliente',

  'recarga.saldo_comprado': 'Compra de saldo',
  'recarga.saldo_contado': 'Saldo contado',
  'recarga.registrada': 'Recarga vendida',

  'empleado.contratado': 'Empleado contratado',
  'empleado.actualizado': 'Empleado modificado',
  'empleado.liquidado': 'Liquidación',
  'empleado.adelanto': 'Adelanto de salario',
  'empleado.vacaciones': 'Vacaciones descansadas',
  'planilla.corrida': 'Planilla corrida',
  'planilla.pagada': 'Planilla pagada',
  'planilla.corregida': 'Planilla corregida',
  'cotizaciones.pagadas': 'Cotizaciones pagadas',
  'aguinaldo.pagado': 'Aguinaldo pagado',
  'aguinaldo.corregido': 'Aguinaldo corregido',

  'gasto.registrado': 'Gasto',
  'faltante.explicado': 'Faltante explicado',
  'gasto.anulado': 'Gasto anulado',
  'gasto.pagado': 'Pago de un gasto a crédito',
  'gasto.pago_anulado': 'Pago de un gasto anulado',
  'dueno.retiro': 'Retiro del dueño',
  'dueno.aporte': 'Aporte del dueño',
  'dueno.movimiento_anulado': 'Dinero del dueño anulado',

  'config.cambiada': 'Configuración cambiada',
};

/** Falls back to the raw type, so an unknown event still reads as something. */
export function etiquetaEvento(tipo: string): string {
  return ETIQUETA_EVENTO[tipo as EventType] ?? tipo;
}

/** Coarse grouping for the bitácora filter. Fewer buckets than event types, on purpose. */
export const GRUPOS_EVENTO = {
  ventas: ['venta.', 'pago.', 'recarga.', 'caja.', 'devolucion.'],
  inventario: ['inventario.', 'compra.', 'producto.', 'unidad_venta.'],
  clientes: ['cliente.', 'pedido.', 'jornada.', 'abono.', 'incobrable.'],
  dinero: ['gasto.', 'dueno.', 'planilla.', 'cotizaciones.', 'aguinaldo.', 'empleado.', 'efectivo.', 'cuenta.', 'banco.', 'semana.', 'faltante.'],
  sistema: ['usuario.', 'sesion.', 'dispositivo.', 'config.', 'lista_precio.', 'precio.', 'oferta.', 'proveedor.'],
} as const;

export type GrupoEvento = keyof typeof GRUPOS_EVENTO;

export function grupoDe(tipo: string): GrupoEvento | null {
  for (const [grupo, prefijos] of Object.entries(GRUPOS_EVENTO)) {
    if (prefijos.some((p) => tipo.startsWith(p))) return grupo as GrupoEvento;
  }
  return null;
}
